/**
 * SPM 前向来源（spm）的候选写入与固化（两段式会话传递）。
 *
 *
 * 需求约定（《存量网页的SPM埋点初始化》《网站 SPM 埋点规范》）：
 * - spm：用户打开页面的前向位置的 SPM，按 session（浏览器窗口生命周期）保存，
 *   不得拼在 URL 后缀（影响 SEO）；
 * - 固化条件（2026-10-09 定版）：只有点击引发路由 path 部分改变时，该点击的坑位
 *   编码才成为下一页的前向来源。同页交互点击（弹窗、收藏、换色、query 筛选/分页、
 *   hash 跳转等）与浏览器回退/前进都不得写入 spm；
 * - 传递范围（2026-10-09 定版）：spm 仅在跳转后的第一个页面有效，无候选的前向
 *   跳转不向后续页面透传；浏览器 history 回退/前进到达的页面，恢复该条目此前
 *   绑定的 spm；刷新页面不改变 spm_refer（刷新前的同页交互候选不得借刷新冒领）；
 * - spm_cnt：当前上报事件的 SPM，由业务调用点通过 withSpmCnt 传入；
 *   模块级曝光传模块编码（A.B.C），坑位级曝光与点击传完整编码（A.B.C.D）。
 *
 * 数据流（两段式）：
 *   ① 点击事件携带 spm_cnt → 写入候选区：sessionStorage 一次性候选（60 秒时效，
 *      覆盖 dev 编译/弱网下的慢速路由切换，超时视为陈旧点击不冒领）+ localStorage
 *      桥接值（60 秒时效，只对明确同源链接目标写入，仅供该目标新文档/新标签继承，
 *      sessionStorage 不跨标签；消费时删除匹配镜像，避免硬导航复活）；
 *      App 内嵌时原生打开 H5 携带的 ?spm= 参数视作最高优先候选（原生→H5 方向的
 *      前向来源，消费后从地址剥离，避免分享/二次跳转携带陈旧来源）；
 *   ② 路由 path 变化（含新文档首载）→ 消费候选：
 *      - 点击引发的跳转：固化为当前页 spm，并绑定到当前 history 条目
 *        （history.state 合并写入，保留 Next.js 内部字段；回退/前进/刷新时浏览器
 *        自动带回该条目的绑定值）；
 *      - 无候选（未接线跳转等）：新条目无绑定，当前页无 spm；
 *      - 浏览器回退/前进（popstate）、条目已绑定的文档重载（刷新/硬加载回退）：
 *        不消费候选，恢复目标条目绑定的 spm；
 *   ③ 本页所有 SPM 相关事件由上报中心注入 spm；本页固化值不随本页后续点击变化；
 *      另经 sensorProvider.registerCommonProperties 注册到神策页面公共属性，
 *      内置全埋点事件（$WebClick/$WebPageLoad 等）也携带站内来源；
 *   ④ App 混合栈闭环（H5→原生方向）：跳转原生页面的桥接方法参数注入 spm
 *      （getSpmForAppNativeJump：取刚点击坑位的 spm_cnt，无候选不传、
 *      不退化当前页来源；由 app-bridge.ts 以 provider 注入 app-communication-manager）；
 *   ⑤ 浏览器唤起 App（OneLink/深链）：唤起链接拼入 spm 参数（getSpmForAppLaunch，
 *      取值口径同上；OneLink 归因参数由原生侧解出）。
 */

import { ZEELOOL_APP_UA_MARKER } from '@/utils/enums/app-sdk';

/** sessionStorage 键：最近一次点击的一次性候选，仅在 path 变化时消费（固化或作废）。 */
const SPM_REFER_CAND_STORAGE_KEY = 'zl_spm_refer:cand';

/**
 * 同标签候选时效：正常点击引发的 SPA 跳转在同一交互内完成，但 dev 编译与弱网下
 * 路由切换可达数十秒——60 秒内到达仍视为点击引发；超时候选按陈旧点击丢弃
 * （宁缺毋冒领）。与跨标签桥接 TTL 保持同一量纲。
 */
const SPM_REFER_CAND_TTL_MS = 60_000;

/** localStorage 桥接键：明确链接候选的跨标签镜像，限来源与目标匹配的一次到达。 */
const SPM_REFER_BRIDGE_STORAGE_KEY = 'zl_spm_refer:last';
const REFER_BRIDGE_VERSION = 1;
const REFER_BRIDGE_TTL_MS = 60_000;

/** history.state 键：当前 history 条目绑定的前向来源 spm，回退/前进/刷新时恢复。 */
const SPM_REFER_HISTORY_KEY = 'zl_spm_refer';

interface ReferCandidatePayload {
  v: number;
  refer: string;
  ts: number;
  sourceUrl?: string;
  targetUrl?: string;
}

let currentPageReferSpm: string | undefined;
let pageReferSpmLoaded = false;
/** 上一次完成固化的 path；与当前 path 不同才视为路由切换（query/hash 变化不算）。 */
let lastPromotedPathname: string | null = null;
/** popstate 置位：下一次 path 变化由浏览器回退/前进引发，恢复条目绑定而非消费候选。 */
let browserNavigationPending = false;
let browserNavListenerAttached = false;
/** 原生 fragment 导航创建 null state 条目，需继承同页 Next 内部状态以支持后续遍历。 */
let currentPageNextHistoryState: Record<string, unknown> = {};
/** 仅当前链接点击分发期间有效；不把上一轮点击目标复用于后续事件。 */
let activeLinkNavigation: { event: Event; sourceUrl: string; targetUrl: string } | undefined;

/** 桥接只比较同源文档路径，query/hash 不构成来源跳转。 */
function getDocumentRoute(url: string): string | undefined {
  try {
    const parsed = new URL(url);
    if (parsed.origin !== window.location.origin) return undefined;
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return undefined;
  }
}

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

/** App WebView 内嵌判定：原生壳在 UA 中注入 nextmar-xms-app（与 spmTerminal 同源标记）。 */
function isInAppWebView(): boolean {
  if (!isBrowser()) return false;
  try {
    return window.navigator.userAgent.includes(ZEELOOL_APP_UA_MARKER);
  } catch {
    return false;
  }
}

/** SPM 编码形态校验：2~4 段点分词字符（对原生侧传入值的防御性校验）。 */
function isValidSpmCode(value: unknown): value is string {
  return typeof value === 'string' && /^[A-Za-z0-9_-]+(\.[A-Za-z0-9_-]+){1,3}$/.test(value);
}

/**
 * App 内嵌时原生打开 H5 携带的 ?spm= 参数：目标页的前向来源（原生→H5 方向）。
 * 仅 App 内嵌读取，非法值忽略；只在新文档首载消费一次（见 ensureReferPromoted）。
 */
function readSpmParamFromUrl(): string | undefined {
  if (!isInAppWebView()) return undefined;
  try {
    const value = new URLSearchParams(window.location.search).get('spm');
    return isValidSpmCode(value) ? value : undefined;
  } catch {
    return undefined;
  }
}

/** 剥离 ?spm= 参数后的当前地址（仅供同文档 replaceState 使用）；无参数时返回 undefined。 */
function buildUrlWithoutSpmParam(): string | undefined {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('spm')) return undefined;
    url.searchParams.delete('spm');
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return undefined;
  }
}

/** 解析候选载荷；损坏、版本不符一律视为无候选。 */
function parseReferCandidate(raw: string | null): ReferCandidatePayload | undefined {
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw) as Partial<ReferCandidatePayload>;
    if (
      parsed?.v !== REFER_BRIDGE_VERSION ||
      typeof parsed.refer !== 'string' ||
      !parsed.refer ||
      typeof parsed.ts !== 'number'
    ) {
      return undefined;
    }
    return parsed as ReferCandidatePayload;
  } catch {
    return undefined;
  }
}

/** 点击 → 候选区：同标签一次性候选；明确的同源链接另写跨标签桥接。 */
function writeReferCandidate(spmCnt: string): void {
  const sourceUrl = getDocumentRoute(window.location.href);
  const navigation = activeLinkNavigation;
  const targetUrl =
    navigation && navigation.event.eventPhase !== 0 && navigation.sourceUrl === sourceUrl
      ? navigation.targetUrl
      : undefined;
  const payload: ReferCandidatePayload = {
    v: REFER_BRIDGE_VERSION,
    refer: spmCnt,
    ts: Date.now(),
    sourceUrl,
    ...(targetUrl ? { targetUrl } : {}),
  };
  const serialized = JSON.stringify(payload);
  try {
    window.sessionStorage.setItem(SPM_REFER_CAND_STORAGE_KEY, serialized);
  } catch {
    // sessionStorage 不可用（隐私模式等）时仅失去本标签的固化来源。
  }
  try {
    // 同标签候选仍可服务程序式导航；跨标签只桥接明确的同源链接目标。
    if (targetUrl) window.localStorage.setItem(SPM_REFER_BRIDGE_STORAGE_KEY, serialized);
  } catch {
    // localStorage 不可用时仅失去跨标签继承。
  }
}

/** 消费来源及目标均匹配的跨标签候选；过期（含时间戳倒挂）视为无候选。 */
function readFreshBridgeRefer(): string | undefined {
  try {
    const raw = window.localStorage.getItem(SPM_REFER_BRIDGE_STORAGE_KEY);
    const payload = parseReferCandidate(raw);
    if (!payload) return undefined;
    const age = Date.now() - payload.ts;
    if (age < 0 || age > REFER_BRIDGE_TTL_MS) return undefined;
    // 新文档必须由记录的来源页面进入记录的目标，地址栏或其他标签不冒领。
    if (
      !payload.sourceUrl ||
      !payload.targetUrl ||
      payload.sourceUrl === payload.targetUrl ||
      payload.sourceUrl !== getDocumentRoute(document.referrer) ||
      payload.targetUrl !== getDocumentRoute(window.location.href)
    ) {
      return undefined;
    }
    if (window.localStorage.getItem(SPM_REFER_BRIDGE_STORAGE_KEY) === raw) {
      window.localStorage.removeItem(SPM_REFER_BRIDGE_STORAGE_KEY);
    }
    return payload.refer;
  } catch {
    return undefined;
  }
}

/** 同文档允许程序式导航；新文档须核对明确目标和实际来源，不能只凭候选时效。 */
function readFreshSessionCandidateRefer(
  targetUrl?: string,
  navigation: 'same-document' | 'document' = 'same-document',
): string | undefined {
  try {
    const payload = parseReferCandidate(window.sessionStorage.getItem(SPM_REFER_CAND_STORAGE_KEY));
    if (!payload) return undefined;
    const age = Date.now() - payload.ts;
    if (age < 0 || age > SPM_REFER_CAND_TTL_MS) return undefined;
    // sessionStorage 可能被新标签从 opener 克隆，不能绕过明确链接目标的约束。
    if (targetUrl && payload.targetUrl && payload.targetUrl !== targetUrl) return undefined;
    if (
      navigation === 'document' &&
      (!payload.sourceUrl ||
        !payload.targetUrl ||
        payload.sourceUrl === payload.targetUrl ||
        payload.sourceUrl !== getDocumentRoute(document.referrer) ||
        payload.targetUrl !== targetUrl)
    ) {
      return undefined;
    }
    return payload.refer;
  } catch {
    return undefined;
  }
}

/** 读当前 history 条目绑定的前向来源 spm；无绑定（新条目）返回 undefined。 */
function readReferFromHistoryEntry(): string | undefined {
  try {
    const state = window.history.state as Record<string, unknown> | null;
    const value = state?.[SPM_REFER_HISTORY_KEY];
    return typeof value === 'string' && value ? value : undefined;
  } catch {
    return undefined;
  }
}

/**
 * 把前向来源绑定到当前 history 条目。必须合并写入、保留 Next.js 写入的内部字段
 * （与 waterFall2ReturnState.ts 同一模式）；nextUrl 用于同一次 replaceState 顺带
 * 改写地址（如剥离 ?spm= 参数）；绑定失败仅影响回退/前进/刷新时的来源恢复，
 * 不影响本页上报。
 */
function bindReferToCurrentHistoryEntry(refer: string, nextUrl?: string): void {
  try {
    const state = window.history.state as Record<string, unknown> | null;
    // 只绑定当前条目的来源及规范化同页 URL，不是路由导航。
    // 原生写入避免重入 SDK/Next 的 history 包装，触发重复自动采集或固化循环。
    window.History.prototype.replaceState.call(
      window.history,
      { ...(state ?? {}), [SPM_REFER_HISTORY_KEY]: refer },
      '',
      nextUrl,
    );
  } catch {
    // history.state 不可用时静默。
  }
}

/** 只保留 Next 路由状态，不把其他业务自定义字段复制到原生 fragment 新条目。 */
function rememberNextHistoryState(): void {
  try {
    const state = window.history.state as Record<string, unknown> | null;
    currentPageNextHistoryState = {};
    for (const key of ['__NA', '__PRIVATE_NEXTJS_INTERNALS_TREE']) {
      if (state && key in state) currentPageNextHistoryState[key] = state[key];
    }
  } catch {
    // history.state 不可用时降级。
  }
}

/** 新交互开始时作废上一交互的候选及其匹配镜像，不影响当前页已绑定来源。 */
function clearPreviousInteractionCandidate(): void {
  activeLinkNavigation = undefined;
  try {
    const candidate = window.sessionStorage.getItem(SPM_REFER_CAND_STORAGE_KEY);
    window.sessionStorage.removeItem(SPM_REFER_CAND_STORAGE_KEY);
    if (candidate && window.localStorage.getItem(SPM_REFER_BRIDGE_STORAGE_KEY) === candidate) {
      window.localStorage.removeItem(SPM_REFER_BRIDGE_STORAGE_KEY);
    }
  } catch {
    // 存储不可用时保持既有降级行为。
  }
}

/** 回退/前进恢复条目来源；同页 history 写入复用 PathnameTracker 的事件保存既有来源。 */
function attachBrowserNavListener(): void {
  if (browserNavListenerAttached || !isBrowser()) return;
  browserNavListenerAttached = true;
  try {
    window.addEventListener('popstate', () => {
      const historyState = window.history.state;
      if (
        lastPromotedPathname === window.location.pathname &&
        (historyState === null || historyState === undefined)
      ) {
        // Next 初始化/程序式导航的条目都有内部状态；同页 null state 是原生 fragment
        // 新条目，沿用当前来源，不消费候选。无来源时也写回 Next 状态，供后续回退识别。
        try {
          window.history.replaceState(
            {
              ...currentPageNextHistoryState,
              ...(currentPageReferSpm ? { [SPM_REFER_HISTORY_KEY]: currentPageReferSpm } : {}),
            },
            '',
          );
        } catch {
          // 宿主不允许写 history 时保持内存来源。
        }
        return;
      }
      browserNavigationPending = true;
      if (lastPromotedPathname === window.location.pathname) ensureReferPromoted();
    });
    window.addEventListener('zl-history-change', () => {
      // 同页条目必须在下一次上报前就保存来源；path 变化仍由既有固化入口处理。
      if (lastPromotedPathname === window.location.pathname) ensureReferPromoted();
    });
    document.addEventListener(
      'click',
      (event) => {
        // 捕获阶段先作废旧交互；本次业务 _Click 在后续处理器写入新候选。
        // 不按任务结束清理，保留同次点击的慢导航 60 秒窗口。
        clearPreviousInteractionCandidate();
        const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null;
        if (!(anchor instanceof HTMLAnchorElement)) return;
        const sourceUrl = getDocumentRoute(window.location.href);
        const targetUrl = getDocumentRoute(anchor.href);
        if (!sourceUrl || !targetUrl || sourceUrl === targetUrl) return;
        const navigation = { event, sourceUrl, targetUrl };
        activeLinkNavigation = navigation;
        // 原生点击的 listener 之间会执行 microtask；延迟到下一任务再释放。
        setTimeout(() => {
          if (activeLinkNavigation === navigation) activeLinkNavigation = undefined;
        }, 0);
      },
      true,
    );
    // 搜索建议也可能通过 Enter 程序式导航，不能借用上一按钮的候选。
    document.addEventListener('keydown', clearPreviousInteractionCandidate, true);
  } catch {
    // 宿主不支持时降级：仅回退/前进标记不可用，固化语义不受影响。
  }
}

/**
 * 当前文档的导航类型（与 waterFall2ReturnState.ts 同一 Performance API 模式）。
 * reload / back_forward 表示本次加载不是新到达（刷新、硬加载式回退前进），
 * 候选不是引发加载的点击，不得消费。
 */
function getNavigationType(): string | undefined {
  try {
    const entry = window.performance?.getEntriesByType?.('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined;
    return entry?.type;
  } catch {
    return undefined;
  }
}

/**
 * 两段式第二段：路由 path 变化（含新文档首载）时消费候选，确定当前页前向来源。
 * 同页 query/hash 导航只保存既有来源，不消费新候选。由 getPageReferSpm/refreshPageReferSpm 惰性调用，
 * 不依赖 effect 执行时序。
 */
function ensureReferPromoted(): void {
  if (!isBrowser()) return;
  attachBrowserNavListener();
  const pathname = window.location.pathname;
  if (lastPromotedPathname === pathname && !browserNavigationPending) {
    // query/hash 导航可能创建无绑定的新条目；保存当前来源供刷新恢复，不消费交互候选。
    if (currentPageReferSpm && readReferFromHistoryEntry() !== currentPageReferSpm) {
      bindReferToCurrentHistoryEntry(currentPageReferSpm);
    }
    rememberNextHistoryState();
    return;
  }
  const wasBrowserNavigation = browserNavigationPending;
  const isDocumentLoad = lastPromotedPathname === null;
  browserNavigationPending = false;

  // 当前条目此前的绑定：刷新/硬加载回退时它就是"之前的 spm_refer"。
  const boundRefer = readReferFromHistoryEntry();
  // 刷新（reload）与硬加载式回退/前进（back_forward）不是"新到达"：
  // 不改变 spm_refer，残留候选（刷新前的同页交互点击）不得借加载冒领。
  const isReloadLikeLoad =
    isDocumentLoad && (getNavigationType() === 'reload' || getNavigationType() === 'back_forward');
  // 候选只在"点击引发的到达"时消费：
  // - 同文档 SPA 跳转：可消费（点击→导航同一交互内发起，60 秒时效兜底慢路由）；
  // - 新文档首载（navigate 型）：条目无绑定，且候选明确目标与当前地址、来源
  //   与 referrer 均匹配时才消费；无目标的同页交互不能借硬导航冒领；
  // - 刷新/reload、back_forward 型加载、浏览器回退/前进：不消费，候选作废。
  const candidateAllowed =
    !wasBrowserNavigation && !isReloadLikeLoad && (!isDocumentLoad || boundRefer === undefined);
  let candidate: string | undefined;
  let consumedUrlSpm = false;
  if (candidateAllowed) {
    if (isDocumentLoad) {
      // App 内嵌：原生打开 H5 时以 ?spm= 传入前向来源，是本次到达的直接原因，
      // 优先于会话候选与跨标签桥接。
      const urlSpm = readSpmParamFromUrl();
      if (urlSpm) {
        candidate = urlSpm;
        consumedUrlSpm = true;
      }
    }
    if (!candidate) {
      candidate = readFreshSessionCandidateRefer(
        getDocumentRoute(window.location.href),
        isDocumentLoad ? 'document' : 'same-document',
      );
    }
    // localStorage 桥接仅服务新文档/新标签继承：同文档 SPA 跳转不得从桥接
    // 复活旧候选（spm 仅在跳转后的第一个页面有效）。
    if (!candidate && isDocumentLoad) {
      candidate = readFreshBridgeRefer();
    }
  }
  // 一次性候选随即作废：固化只发生一次。
  try {
    const sessionCandidate = window.sessionStorage.getItem(SPM_REFER_CAND_STORAGE_KEY);
    window.sessionStorage.removeItem(SPM_REFER_CAND_STORAGE_KEY);
    // 同标签到达也消费其镜像；只删除同一候选，不影响其他标签新写入的链接。
    if (
      sessionCandidate &&
      window.localStorage.getItem(SPM_REFER_BRIDGE_STORAGE_KEY) === sessionCandidate
    ) {
      window.localStorage.removeItem(SPM_REFER_BRIDGE_STORAGE_KEY);
    }
  } catch {
    // 存储不可用时静默。
  }

  if (candidate) {
    // 点击引发的跳转：固化为当前页 spm，并绑定到当前 history 条目；
    // App 内嵌消费 URL ?spm= 时同一次 replaceState 顺带剥离参数。
    bindReferToCurrentHistoryEntry(
      candidate,
      consumedUrlSpm ? buildUrlWithoutSpmParam() : undefined,
    );
    currentPageReferSpm = candidate;
  } else {
    // 无候选/非点击到达：取条目绑定——新条目无绑定即无来源（spm 仅在跳转后的
    // 第一个页面有效）；回退/前进/刷新恢复该条目此前的绑定。
    currentPageReferSpm = boundRefer;
  }
  lastPromotedPathname = pathname;
  pageReferSpmLoaded = true;
  rememberNextHistoryState();
}

/**
 * 记录一次坑位点击（spm_cnt 为 A.B.C.D）到候选区：只有在后续路由 path 部分变化时，
 * 该候选才固化为目标页面的前向来源（见文件头两段式说明）。同页交互点击不污染来源。
 * 明确同源链接点击同时写 localStorage 桥接值，供对应新标签在 60 秒内一次继承。
 */
export function captureSpmClickRefer(spmCnt: string): void {
  if (!isBrowser() || !spmCnt) return;
  writeReferCandidate(spmCnt);
}

/** 路由变化或首次加载时调用：确定当前页前向来源（固化候选或恢复条目绑定）。 */
export function refreshPageReferSpm(): string | undefined {
  ensureReferPromoted();
  if (!pageReferSpmLoaded) {
    // 非浏览器环境兜底：SSR 无会话，前向来源恒为 undefined，不虚构。
    currentPageReferSpm = undefined;
  }
  return currentPageReferSpm;
}

/** 当前页前向来源 spm；会话首屏无来源时返回 undefined（不虚构占位值）。 */
export function getPageReferSpm(): string | undefined {
  ensureReferPromoted();
  if (!pageReferSpmLoaded) {
    currentPageReferSpm = undefined;
  }
  return currentPageReferSpm;
}

/** 仅供测试：重置模块级会话状态（path 固化标记、回退标记、内存 refer）。 */
export function resetSpmReferStateForTests(): void {
  currentPageReferSpm = undefined;
  pageReferSpmLoaded = false;
  lastPromotedPathname = null;
  browserNavigationPending = false;
  currentPageNextHistoryState = {};
}

/**
 * H5 跳转 App 原生页面时透传的 spm 参数（App 混合栈闭环的 H5→原生方向）：
 * 取值 = 刚点击的坑位候选（spm_cnt，60 秒内）。点击未接线（无候选）时不传，
 * 不退化传当前页前向来源——目标页的来源是"点击的坑位"，不是本页自己的来源
 * （2026-10-09 定版）；非 App 内嵌恒为 undefined。
 */
export function getSpmForAppNativeJump(): string | undefined {
  if (!isBrowser() || !isInAppWebView()) return undefined;
  return readFreshSessionCandidateRefer();
}

/**
 * 浏览器 H5 通过 OneLink/深链唤起 App 时透传的 spm 参数：取值 = 刚点击的坑位
 * 候选（spm_cnt，60 秒内），无候选（点击未接线）不传——同 getSpmForAppNativeJump
 * 的定版口径，不退化传当前页前向来源。唤起只发生在浏览器，不做内嵌门禁。
 */
export function getSpmForAppLaunch(): string | undefined {
  if (!isBrowser()) return undefined;
  return readFreshSessionCandidateRefer();
}

/**
 * 观鹿自动采集事件（pageView/webClick/webExp/PageLeave）的页面级来源属性合并：
 * 把当前页前向来源 spm 写入事件属性，与神策 registerPage 的全局注入同语义。
 * 业务已显式传 spm 时不覆盖；无来源时原样返回/返回 undefined（不拼空字段，
 * 调用点保持无来源时的原始载荷形状）。
 */
export function withSpmPageRefer<T extends object>(properties: T): T;
export function withSpmPageRefer(): Record<string, string> | undefined;
export function withSpmPageRefer(properties?: object): object | undefined {
  const refer = getPageReferSpm();
  if (!properties) return refer ? { spm: refer } : undefined;
  if ('spm' in properties) return properties;
  return refer ? { spm: refer, ...properties } : properties;
}

/**
 * 仓库埋点事件命名约定（《网站 SPM 埋点规范》）：点击事件名以 _Click 结尾（如 TopNavTab_Click）。
 * 该后缀同时是候选区的写入门闸：承载 spm_cnt 但命名不带 _Click 后缀的点击
 * （如历史遗留的 XxxClick）不会进入候选区，后续 path 变化也就无来源可固化——
 * 新增点击事件必须遵守命名约定。曝光/点击均可能带 D 位编码，故不能放宽为
 * 后缀 click 或 D 段计数。
 */
export function isSpmClickEvent(eventName?: string): boolean {
  return typeof eventName === 'string' && eventName.toLowerCase().endsWith('_click');
}

interface SpmTrackingEventLike {
  eventName?: string;
  properties?: unknown;
}

/** 适配结果合并调用时已捕获的来源；纯合并，不读取路由或重复写点击候选。 */
export function withCapturedSpmRefer<T extends SpmTrackingEventLike>(
  event: T,
  refer: string | undefined,
): T {
  if (!refer) return event;
  const properties = event.properties;
  if (!properties || typeof properties !== 'object' || Array.isArray(properties)) {
    return { ...event, properties: { spm: refer } };
  }
  if ('spm' in properties) return event;
  return { ...event, properties: { ...properties, spm: refer } };
}

/**
 * 为事件附加 SPM 属性（不修改调用方对象，返回原事件或浅拷贝）：
 * - 点击事件携带 spm_cnt 时，把该坑位写入候选区，供下一次 path 变化固化；
 * - 业务已显式传 spm 时不覆盖；
 * - 存在前向来源时注入 spm；无来源（会话首屏等）时保持原样。
 */
export function attachSpmTracking<T extends SpmTrackingEventLike>(event: T): T {
  const properties = event.properties;
  const spmCnt =
    properties && typeof properties === 'object' && !Array.isArray(properties)
      ? (properties as Record<string, unknown>).spm_cnt
      : undefined;
  if (typeof spmCnt === 'string' && spmCnt && isSpmClickEvent(event.eventName)) {
    captureSpmClickRefer(spmCnt);
  }

  const refer = getPageReferSpm();
  if (!refer) return event;
  if (!properties || typeof properties !== 'object' || Array.isArray(properties)) {
    return { ...event, properties: { spm: refer } };
  }
  if ('spm' in properties) return event;
  return { ...event, properties: { ...(properties as Record<string, unknown>), spm: refer } };
}

/** 业务调用点帮助函数：把坑位编码（D 位）或模块编码（C 位）附加到事件 properties。 */
export function withSpmCnt<T extends object>(
  properties: T,
  spmCnt: string,
): T & { spm_cnt: string } {
  return { ...properties, spm_cnt: spmCnt };
}
